import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { Nullable } from '~/types/Nullable';
import {
  CustomAttributes as CustomAttributesType,
  FormField as FormFieldType,
} from '~/types/SSOForm';

type Props = {
  formField: FormFieldType<CustomAttributesType>;
  initialValue?: Record<string, string>;
  onExplain?: (fieldId: string) => void;
  onChange: (value: Record<string, string>) => void;
};

type CustomAttribute = {
  name: Nullable<string>;
  label: Nullable<string>;
};

export const CustomAttributes = ({
  formField,
  initialValue,
  onExplain,
  onChange,
}: Props) => {
  const [customAttributes, setCustomAttributes] = useState<CustomAttribute[]>(
    [],
  );
  const isPremiumAdministrationEnabled = useFlag(
    FlagType.PremiumAdministration,
  );

  const intl = useIntl();

  const premiumFeatureTooltip = intl.formatMessage({
    id: 'CustomAttributes.premiumFeatureTooltip',
    defaultMessage: 'Available with the Advanced Administration add-on.',
    description: 'Tooltip for premium features',
  });

  const fieldRequiredError = intl.formatMessage({
    id: 'CustomAttributes.fieldRequiredError',
    defaultMessage: 'This field is required',
    description: 'Error message when field is required',
  });

  const handleAddNewAttribute = () => {
    setCustomAttributes([...customAttributes, { name: null, label: null }]);
    onChange(
      customAttributes.reduce(
        (acc, { name, label }) => ({ ...acc, [name ?? '']: label }),
        {},
      ),
    );
  };

  const handleRemoveAttribute = (index: number) => {
    const newCustomAttributes = [...customAttributes];
    newCustomAttributes.splice(index, 1);
    setCustomAttributes(newCustomAttributes);
    onChange(
      newCustomAttributes.reduce(
        (acc, { name, label }) => ({ ...acc, [name ?? '']: label }),
        {},
      ),
    );
  };

  const handleSetName = (index: number, name: string) => {
    const newCustomAttributes = [...customAttributes];
    newCustomAttributes[index].name = name;
    setCustomAttributes(newCustomAttributes);
    onChange(
      newCustomAttributes.reduce(
        (acc, { name, label }) => ({ ...acc, [name ?? '']: label }),
        {},
      ),
    );
  };

  const handleSetLabel = (index: number, label: string) => {
    const newCustomAttributes = [...customAttributes];
    newCustomAttributes[index].label = label;
    setCustomAttributes(newCustomAttributes);
    onChange(
      newCustomAttributes.reduce(
        (acc, { name, label }) => ({ ...acc, [name ?? '']: label }),
        {},
      ),
    );
  };

  useEffect(() => {
    if (initialValue && Object.keys(initialValue).length > 0) {
      setCustomAttributes(
        Object.entries(initialValue).map(([name, label]) => ({ name, label })),
      );
    }
  }, [initialValue]);

  return (
    <Layout direction="column" minW="input.min-width" spacing={2} w="100%">
      <Layout direction="column">
        <Layout>
          <Text variant="section">{formField.title}</Text>
          {onExplain && (
            <Button
              text={intl.formatMessage({
                id: 'CustomAttributes.explainButton',
                defaultMessage: 'Explain this',
                description: 'Button text to explain the field',
              })}
              variant="action"
              onClick={() => onExplain(formField.fieldName)}
            />
          )}
        </Layout>
        <Text preset="description">{formField.description}</Text>
      </Layout>
      <Layout direction="column" spacing={4}>
        {customAttributes.map((customAttribute, i) => (
          <Layout key={`custom_attribute_${i}`}>
            <Layout flex="1" mr={4}>
              <FormField
                input={TextInput}
                placeholder={intl.formatMessage({
                  id: 'CustomAttributes.textOrUrlPlaceholder',
                  defaultMessage: 'Text or URL',
                  description: 'Placeholder for text or URL input',
                })}
                label={
                  i === 0
                    ? intl.formatMessage({
                        id: 'CustomAttributes.idpAttributeNameLabel',
                        defaultMessage: 'IDP attribute name',
                        description: 'Label for IDP attribute name field',
                      })
                    : ''
                }
                description={
                  i === 0
                    ? intl.formatMessage({
                        id: 'CustomAttributes.idpAttributeDescription',
                        defaultMessage: 'Copy and paste from your IDP',
                        description: 'Description for IDP attribute name field',
                      })
                    : ''
                }
                name="customAttributeName"
                value={customAttribute.name}
                disabled={!isPremiumAdministrationEnabled}
                tooltip={
                  isPremiumAdministrationEnabled
                    ? undefined
                    : premiumFeatureTooltip
                }
                onChange={(name) => handleSetName(i, name as string)}
                error={
                  customAttribute.name === '' ? fieldRequiredError : undefined
                }
              />
            </Layout>

            <Layout flex="1" mr={4}>
              <FormField
                input={TextInput}
                placeholder={intl.formatMessage({
                  id: 'CustomAttributes.displayNamePlaceholder',
                  defaultMessage: 'Eg: User ID, Manager',
                  description: 'Placeholder for display name input',
                })}
                label={
                  i === 0
                    ? intl.formatMessage({
                        id: 'CustomAttributes.evisortDisplayNameLabel',
                        defaultMessage: 'Evisort display name',
                        description: 'Label for Evisort display name field',
                      })
                    : ''
                }
                description={
                  i === 0
                    ? intl.formatMessage({
                        id: 'CustomAttributes.displayNameDescription',
                        defaultMessage:
                          'This is used for display purposes only',
                        description: 'Description for display name field',
                      })
                    : ''
                }
                name="customAttributeLabel"
                value={customAttribute.label}
                disabled={!isPremiumAdministrationEnabled}
                tooltip={
                  isPremiumAdministrationEnabled
                    ? undefined
                    : premiumFeatureTooltip
                }
                onChange={(label) => handleSetLabel(i, label as string)}
                error={
                  customAttribute.label === '' ? fieldRequiredError : undefined
                }
              />
            </Layout>
            {isPremiumAdministrationEnabled && (
              <Box mt={i === 0 ? 12 : 1}>
                <IconButton
                  text={intl.formatMessage({
                    id: 'CustomAttributes.removeButton',
                    defaultMessage: 'Remove',
                    description: 'Button text to remove attribute',
                  })}
                  icon="trash"
                  onClick={() => handleRemoveAttribute(i)}
                />
              </Box>
            )}
          </Layout>
        ))}
        <Layout>
          <Button
            disabled={!isPremiumAdministrationEnabled}
            text={intl.formatMessage({
              id: 'CustomAttributes.addCustomAttributeButton',
              defaultMessage: 'Add Custom Attribute',
              description: 'Button text to add new custom attribute',
            })}
            tooltip={
              isPremiumAdministrationEnabled ? undefined : premiumFeatureTooltip
            }
            variant="secondary"
            onClick={handleAddNewAttribute}
          />
        </Layout>
      </Layout>
    </Layout>
  );
};
